import { useState, useEffect, useMemo, useCallback } from 'react';
import { Provider } from '@ethersproject/providers';
import { Contract } from '@ethersproject/contracts';
import { useWeb3React } from './web3';
import { getContract } from '../utils';
import { contracts } from 'web3/utils/contracts';

export function useContract(
  name: string | undefined,
  address: string | undefined,
  provider?: Provider
): Contract | undefined {
  const [contract, setContract] = useState<Contract | undefined>();
  const { library, active } = useWeb3React();

  useEffect(() => {
    const _fetchContract = async (_name: string, _address: string) => {
      if (!library) return;
      const contract = getContract(
        _address,
        contracts[_name].abi,
        library.getUncheckedSigner()
      );
      setContract(contract);
    };
    if (name && address && library && active) _fetchContract(name, address);
  }, [name, address, provider, library, active]);
  return contract;
}

export function useBlockNumber(): number | undefined {
  const { chainId, library, active } = useWeb3React();
  const [blockNumber, setBlockNumber] = useState<number | undefined>();
  useEffect(() => {
    let stale = false;
    const watchNumber = async () => {
      try {
        library?.on('block', updateBlockNumber);
        const blockNumber = await library?.getBlockNumber();

        setBlockNumber(blockNumber);
      } catch (err) {
        setBlockNumber(undefined);
      }
    };

    const updateBlockNumber = (blockNumber: number) => {
      setBlockNumber(blockNumber);
    };

    if (active && library && !stale) watchNumber();

    return () => {
      stale = true;
      library && library.removeListener('block', updateBlockNumber);
      setBlockNumber(undefined);
    };
  }, [chainId, active]);
  return useMemo(() => blockNumber, [blockNumber]);
}

interface AsyncRes {
  res?: any;
  error?: string;
  loading: boolean;
}

/**
 * allows us to easily call async functions inside hooks
 * @param  {Function} fn         function we want to call
 * @param  {Array} args       fn arguments
 * @param  {opt}  { condition: only execute if condition is true, deps: additional execution deps }
 * @return {error: String, result: Any, loading: boolean} return object
 */
export function useAsync(fn, args: any[], opt: any = {}): AsyncRes {
  const defaultOpt = { condition: true, deps: [] };
  opt = { ...defaultOpt, ...opt };
  const [res, setResult] = useState<any | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const callAsyncFn = async () => {
      try {
        setLoading(true);
        const result = await fn(...args);
        setResult(result);
        setLoading(false);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        if (err instanceof Error) setError(err.message);
        setLoading(false);
      }
    };
    if (opt.condition && fn) callAsyncFn();
  }, [opt.condition, ...args, ...opt.deps, fn]);
  return { error, res, loading };
}

export function useContractParam(contract, param, args) {
  const block = useBlockNumber();
  const fn = useCallback(contract?.[param], [contract, param]);
  return useAsync(fn, args, { condition: !!contract, deps: [block] });
}
